import React, { useEffect, useState, cloneElement, ReactElement, ChangeEvent } from 'react';

import { observer } from 'mobx-react-lite';
import { useStore } from '@vromo/mobx-react/mobx-react';

import { LocalStorage } from '@vromo/models';

import useRouter from '@vromo/hooks/useRouter';

import Modal, {
    ModalHeader,
    ModalContent,
    ModalActions,
    MODAL_CLOSE_TIMEOUT
} from '@vromo/ui/components/Modal';
import Title from '@vromo/ui/components/Title';
import Button from '@vromo/ui/components/Button';
import { useToasts } from '@vromo/ui/components/Toasts';

export const UpgradeWrapper = observer<{ isRequest?: boolean; children: ReactElement }>(
    function UpgradeWrapper({ isRequest = false, children }) {
        const { authStore } = useStore('rootStore');
        const isFreemium = authStore.isFreemium;

        const [open, setOpen] = useState(false);

        const upgradeText = isRequest
            ? 'To request an upgrade for your account, click the Upgrade button below.'
            : 'This is a premium feature. To request an upgrade for your account, click the Upgrade button below.';

        return (
            <>
                {cloneElement(children, {
                    ...(isFreemium && {
                        onClick: (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setOpen(true);
                        }
                    })
                })}

                <UpgradeModal
                    isOpen={open}
                    upgradeText={upgradeText}
                    onClose={() => setOpen(false)}
                />
            </>
        );
    }
);

export const UpgradeModal = observer<{
    isOpen: boolean;
    upgradeText: string;
    onClose: ChangeEvent;
}>(function UpgradeModal({ isOpen, upgradeText, onClose }) {
    const { authStore } = useStore('rootStore');

    const [upgradeRequested, setUpgradeRequested] = useState(false);

    useEffect(() => {
        setUpgradeRequested(
            LocalStorage.upgradeRequested !== undefined ? LocalStorage.upgradeRequested : false
        );
    }, []);

    const showToast = useToasts();

    const requestUpgrade = () => {
        const {
            user: { id, contact },
            role: { group }
        } = authStore;

        fetch('https://hooks.zapier.com/hooks/catch/1689202/o4o1fzz/', {
            method: 'POST',
            body: JSON.stringify({
                id: id,
                email: contact?.email,
                name: contact?.name,
                phone: contact?.phone,
                group: group?.name
            })
        })
            .then((_) => {
                setUpgradeRequested(true);
                LocalStorage.upgradeRequested = true;
            })
            .catch((e) => {
                console.error(e);
                showToast('Something went wrong while requesting your upgrade. Please try again.');
            });
    };

    return (
        <>
            <Modal isOpen={isOpen && !upgradeRequested} onRequestClose={onClose}>
                <ModalHeader>
                    <Title>Upgrade to Premium</Title>
                </ModalHeader>
                <ModalContent style={{ maxWidth: 500 }}>
                    <div>{upgradeText}</div>
                </ModalContent>
                <ModalActions style={{ justifyContent: 'center' }}>
                    <Button primary onClick={requestUpgrade}>
                        Upgrade
                    </Button>
                </ModalActions>
            </Modal>
            <Modal isOpen={isOpen && upgradeRequested} onRequestClose={onClose}>
                <ModalHeader>
                    <Title>Upgrade Requested</Title>
                </ModalHeader>
                <ModalContent style={{ maxWidth: 500 }}>
                    <div>
                        You've successfully requested an upgrade! A member from our team will
                        contact you as soon as possible with more details.
                    </div>
                </ModalContent>
                <ModalActions>
                    <Button onClick={onClose}>Close</Button>
                </ModalActions>
            </Modal>
        </>
    );
});

export const UpgradePage = observer(function UpgradePage() {
    const [open, setOpen] = useState(true);
    const { history } = useRouter();

    return (
        <div>
            <UpgradeModal
                isOpen={open}
                onClose={() => {
                    setOpen(false);
                    setTimeout(() => history.push('/'), MODAL_CLOSE_TIMEOUT);
                }}
            />
        </div>
    );
});
